// frontend/src/features/drivers/DriverSlice.js
// Responsibility: Keep a single, normalized source of truth for drivers across all tabs.
// This Redux slice maintains a normalized drivers store, provides rich selectors (by status/agency/filter),
// upsert/remove and status-patch helpers, and computes counts for dashboards.

import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import {
  uploadDriverPhoto as svcUpload,
  deleteDriverPhoto as svcDelete,
} from '../../services/driverService';

// ============================================================================
// Async thunks (side effects / API calls)
// ============================================================================
// A thunk is an async action creator. It lets your UI "dispatch work" that can
// take time (like an HTTP request). When it succeeds or fails, Redux Toolkit
// automatically dispatches lifecycle actions: pending/fulfilled/rejected.

// Returns the UPDATED driver from the server after upload complete
export const uploadDriverPhoto = createAsyncThunk(
  'drivers/uploadPhoto',
  // payloadCreator receives the arguments you dispatch with:
  // dispatch(uploadDriverPhoto({ id, file }))
  async ({ id, file }) => {
    // Call your service; it should return the updated driver object
    const updatedDriver = await svcUpload(id, file);
    return updatedDriver;
  }
);

// Returns the UPDATED driver (with photo removed) from the server
export const removeDriverPhoto = createAsyncThunk(
  'drivers/removePhoto',
  async ({ id }) => {
    const updatedDriver = await svcDelete(id);
    return updatedDriver;
  }
);

// ============================================================================
// Entity adapter (normalized store) + initial state
// ============================================================================
// The adapter gives you helpers (setAll, upsertOne, etc.) AND memoized selectors.
// We sort by name for a stable, user-friendly list.
const driversAdapter = createEntityAdapter({
  selectId: (driver) => driver.id,
  sortComparer: (a, b) => {
    const an = (a?.name ?? '').toString();
    const bn = (b?.name ?? '').toString();
    return an.localeCompare(bn);
  },
});

const initialState = driversAdapter.getInitialState();

// ============================================================================
// Slice: reducers (sync state changes) + extraReducers (react to thunks)
// ============================================================================
// - "reducers": local actions you define (pure, synchronous state updates)
// - "extraReducers": how this slice reacts to OTHER actions, like thunk results
const driversSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    // Destructive replace (avoid on UPS pages; prefer upserts)
    setDrivers: driversAdapter.setAll,

    // Merge-friendly updates
    upsertDriver: driversAdapter.upsertOne,
    upsertDrivers: driversAdapter.upsertMany,

    // Remove only on hard delete
    removeDriver: driversAdapter.removeOne,
  },
  extraReducers: (builder) => {
    // When the upload/remove thunks complete successfully, the server
    // returns an updated driver object — we merge it in.
    builder
      .addCase(uploadDriverPhoto.fulfilled, (state, action) => {
        driversAdapter.upsertOne(state, action.payload);
      })
      .addCase(removeDriverPhoto.fulfilled, (state, action) => {
        driversAdapter.upsertOne(state, action.payload);
      });
  },
});

// Export actions for components to dispatch
export const { setDrivers, upsertDriver, upsertDrivers, removeDriver } =
  driversSlice.actions;

// Export reducer to add to your store
export default driversSlice.reducer;

// ============================================================================
// Base selectors from the adapter
// ============================================================================
export const {
  selectAll: selectAllDrivers, // array (sorted by adapter.sortComparer)
  selectById: selectDriverById, // driver by id
  selectEntities: selectDriverMap, // id -> driver
  selectIds: selectDriverIds, // [id, id, ...]
  selectTotal: selectDriverTotal, // number
} = driversAdapter.getSelectors((state) => state.drivers);

// ============================================================================
// Helpers (shared logic for selectors)
// ============================================================================
// ============================================================================
// Convenience selectors
// ============================================================================

// Legacy driver metadata/cache selector.
// This filters plain driver `status` only; it is NOT operational lane truth.
// UPS lanes must use assignment_status, ban-backed reads, and blocked/orphaned reads.
export const selectDriversByStatus = (status) =>
  createSelector(selectAllDrivers, (drivers) =>
    drivers.filter((d) => d.status === status)
  );

// ============================================================================
// Counters
// ============================================================================
// Legacy driver metadata/cache tally.
// These counts are based on plain driver `status`; they are NOT operational lane truth.
// UPS lanes must use assignment_status, ban-backed reads, and blocked/orphaned reads.
const tally = (list) => {
  const counts = { Total: 0, Active: 0, Reserved: 0, Banned: 0 };
  for (const d of list) {
    counts.Total += 1;
    if (d?.status && counts[d.status] != null) counts[d.status] += 1;
  }
  return counts;
};

// legacy fallback – safe to delete after Phase 1 validation
// Legacy global search grouped by driver metadata/cache `status`.
// This is NOT operational lane truth; UPS lanes must use assignment_status,
// ban-backed reads, and blocked/orphaned reads.
export const selectLegacyDriverStatusGlobalResults = createSelector(
  [selectAllDrivers, (state) => state.ui?.filters || {}],
  (drivers, { agencyId, q }) => {
    const aid = agencyId ? Number(agencyId) : undefined;
    const query = (q || '').trim().toLowerCase();
    const base = drivers.filter((d) => {
      if (aid !== undefined && Number(d.agency_id) !== aid) return false;
      if (!query) return false;
      const hay = `${d.name || ''} ${d.email || ''} ${
        d.vehicle_registration_number || ''
      }`.toLowerCase();
      return hay.includes(query);
    });
    return {
      Active: base.filter((d) => d.status === 'Active'),
      Reserved: base.filter((d) => d.status === 'Reserved'),
      Banned: base.filter((d) => d.status === 'Banned'),
    };
  }
);
